/**
 * This Component is intentionally light. It exists separately
 * to ease server side rendering.
 */
import React, {useEffect, useMemo, useState} from 'react';
import App from './App';
import styled, {ThemeProvider} from 'styled-components';
import {getTheme} from '@ey/styled-theme';
import {useIsDrawerMode} from '../util/Ares/customHooks';
import {useUserPreferences} from '../util/customHooks';
import EntryPoint from './EntryPoint';
import AppStartup from './AppStartup';
import {initialize} from '../startup';

function Routes({path} = {}) {
	const [theme, setTheme] = useState(undefined);

	const userPreferences = useUserPreferences();
	const isDrawerMode = useIsDrawerMode();

	// When Hercules embeds this app in a drawer it passes a `path`. In that case we route with
	// an isolated in-memory router (see App -> createMemoryRouter) so the drawer's routing does
	// NOT mutate the shared browser history and interfere with the main app instance behind it.
	const useMemoryRouting = Boolean(path);

	let pathName;
	if (useMemoryRouting) {
		try {
			pathName = new URL(path, window.location.origin).pathname;
		} catch {
			pathName = path;
		}
	} else {
		pathName = window.location.pathname;
	}
	pathName = pathName.startsWith('/') ? pathName : '/' + pathName;

	useEffect(() => {
		if (userPreferences && userPreferences.isPreferencesLoaded === true) {
			const preferences = userPreferences?.preferences;
			if (preferences) {
				setTheme(preferences.theme ?? 'light');

				document.getElementsByTagName('body')[0].setAttribute('motif-theme', preferences.theme ?? 'light');
				document.getElementsByTagName('body')[0].setAttribute('class', preferences?.theme === 'light' ? 'motif-theme-light' : 'motif-theme-dark');
			}
		}
	}, [userPreferences]);

	useEffect(() => {
		setTheme('light');
		document.getElementsByTagName('body')[0].setAttribute('motif-theme', 'light');
		document.getElementsByTagName('body')[0].setAttribute('class', 'motif-theme-light');
	}, []);

	useEffect(() => {
		const body = document.getElementsByTagName('body')[0];
		if (!body) return;

		if (isDrawerMode) {
			body.setAttribute('data-drawer-mode', 'true');
		} else {
			body.removeAttribute('data-drawer-mode');
		}

		return () => {
			body.removeAttribute('data-drawer-mode');
		};
	}, [isDrawerMode]);

	/**
	 * To configure a new route, add a new element with
	 *   - show: Function that takes the path as input, and returns true if the component declared in the render prop should be shown
	 *   - render: JSX fragment (component) to render if show returns true
	 */
	const componentMapping = useMemo(() => {
		return [
			// FIT v2 Minimalistic container
			{
				show: (routePath) => routePath.startsWith('/guidedworkflowv2/minimal'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasError hasMemoryRouting={useMemoryRouting} path={path} />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// FIT v2 container
			{
				show: (routePath) => routePath.startsWith('/guidedworkflowv2'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasNavigation hasError hasMemoryRouting={useMemoryRouting} path={path} />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// FIT v1 container
			{
				show: (routePath) => routePath.startsWith('/guidedworkflow'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasNavigation hasError hasMemoryRouting={useMemoryRouting} path={path} />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// Standalone
			{
				show: (routePath) => routePath.startsWith('/standalone'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasMemoryRouting={useMemoryRouting} path={path} />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			}
		];
	}, [isDrawerMode, theme, useMemoryRouting, path]);

	const routeContent = useMemo(() => {
		return componentMapping.find((mapping) => mapping.show(pathName));
	}, [componentMapping]);

	if (routeContent) {
		return routeContent.render;
	}

	// Canvas Forms (Kronos) -- Default view
	return (
		<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
			<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
				<App hasNavigation hasError hasMemoryRouting={useMemoryRouting} path={path} />
			</StyledLandingContainer>
		</ThemeProvider>
	);
}

export default EntryPoint(AppStartup(Routes, {initialize}));

const StyledLandingContainer = styled.section`
	&.rootWrapper {
		.container-fluid {
			margin: 0;
		}
	}
`;
